import { Badge, BadgeProps, BlockStack, Link, Text } from '@shopify/polaris';
import { Campaign } from '../../../../utils/interfaces/campaign';
import {
  computeSnapshotStatus,
  formatSnapshotTime,
  SnapshotStatus,
  SUPPORT_URL,
} from '../../../../utils/snapshotStatus';

const BADGE_TONE: Partial<Record<SnapshotStatus['state'], BadgeProps['tone']>> = {
  recorded: 'success',
  scheduled: 'info',
  delayed: 'warning',
  expired: 'critical',
};

const BADGE_LABEL: Partial<Record<SnapshotStatus['state'], string>> = {
  recorded: 'Recorded',
  scheduled: 'Scheduled',
  recording: 'In process',
  delayed: 'Delayed',
  expired: 'Not recorded',
};

/**
 * The "Snapshot" row of the gate header card.
 *
 * A merchant needs two things from this row: the moment holders are counted at,
 * and whether we have actually counted them yet. The server retries a stuck
 * snapshot on its own, so there is no button — only a support link once a full
 * day has gone by.
 */
export const ShopifySnapshotStatus: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
  if (!campaign.snapshot) return <span>No</span>;

  const status = computeSnapshotStatus(campaign.snapshotData);

  // Snapshot is on but the server told us nothing about it: say so plainly
  // rather than inventing a time.
  if (!status) return <span>Yes</span>;

  return (
    <BlockStack gap="100">
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span>{formatSnapshotTime(status.scheduledAt)}</span>
        {BADGE_LABEL[status.state] && (
          <Badge tone={BADGE_TONE[status.state]}>{BADGE_LABEL[status.state]!}</Badge>
        )}
      </span>

      {status.state === 'recorded' && status.completedAt && (
        <Text as="p" tone="subdued" variant="bodySm">
          Recorded {formatSnapshotTime(status.completedAt)}
        </Text>
      )}

      {status.state === 'recording' && (
        <Text as="p" tone="subdued" variant="bodySm">
          Usually ready ~30 min after the snapshot time.
        </Text>
      )}

      {status.state === 'delayed' && (
        <Text as="p" tone="subdued" variant="bodySm">
          Taking longer than usual. We retry automatically.
        </Text>
      )}

      {status.state === 'expired' && (
        <Text as="p" tone="subdued" variant="bodySm">
          <Link url={SUPPORT_URL} target="_blank">
            Contact Manifold support
          </Link>
        </Text>
      )}
    </BlockStack>
  );
};
